import { useStringForPlaceholders } from "@1js/localization-components";
import {
  Avatar,
  Tag,
  Tooltip,
  makeStyles,
  shorthands,
} from "@fluentui/react-components";
import React from "react";
import type { Prompt } from "../data";
import { PromptAuthorLabel } from "./PromptAuthor.strings";

export type PromptAuthorProps = {
  prompt: Prompt;
  expanded?: boolean;
  size?: "extra-small" | "small" | "medium";
  tabIndex?: number;
};

const MicrosoftLogo: React.FC<{ className: string }> = ({ className }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 23 23"
      className={className}
    >
      <path fill="#f3f3f3" d="M0 0h23v23H0z" />
      <path fill="#f35325" d="M1 1h10v10H1z" />
      <path fill="#81bc06" d="M12 1h10v10H12z" />
      <path fill="#05a6f0" d="M1 12h10v10H1z" />
      <path fill="#ffba08" d="M12 12h10v10H12z" />
    </svg>
  );
};

const useStyles = makeStyles({
  microsoftAvatarContainer: {
    height: "16px",
    width: "20px",
  },
  avatar: {
    marginLeft: "4px",
    height: "16px",
    width: "16px",
  },
  iconOnly: {
    paddingRight: "0px",
    marginRight: "0px",
  },
  srOnly: {
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: "auto",
    position: "absolute",
    width: "1px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    ...shorthands.border("0px"),
    ...shorthands.margin("0px"),
    ...shorthands.padding("0px"),
  },
});

export const PromptAuthor: React.FC<PromptAuthorProps> = React.memo(
  ({ prompt, size, expanded, tabIndex }) => {
    const styles = useStyles();
    const authorLabel = useStringForPlaceholders(PromptAuthorLabel);
    let displayName = "Microsoft";
    let avatar = (
      <div
        data-testid="prompt-author-avatar"
        className={styles.microsoftAvatarContainer}
      >
        <MicrosoftLogo className={styles.avatar} />
      </div>
    );
    if (prompt.Origin === "User") {
      if (!prompt.Author) {
        // there is no way to render the author if the prompt is a user prompt and the author is not available
        return null;
      } else {
        displayName = prompt.Author.DisplayName;
        avatar = (
          <Avatar
            image={{ src: prompt.Author.Avatar }}
            name={displayName}
            shape={"circular"}
            size={16}
            className={styles.avatar}
            aria-hidden={true}
            data-testid="prompt-author-avatar"
          />
        );
      }
    }

    if (expanded) {
      return (
        <Tag
          tabIndex={tabIndex ?? 0}
          size={size ?? "small"}
          media={avatar}
          data-testid="prompt-author-tag"
        >
          <span aria-hidden={true}>{displayName}</span>
          <span className={styles.srOnly}>
            {authorLabel({ authorName: displayName })}
          </span>
        </Tag>
      );
    }

    return (
      <Tooltip content={displayName} relationship="label">
        <Tag
          tabIndex={tabIndex ?? 0}
          data-testid="prompt-author"
          size={size ?? "small"}
          media={avatar}
          aria-label={authorLabel({ authorName: displayName })}
          className={styles.iconOnly}
          primaryText={null}
        >
          <span className={styles.srOnly}>
            {authorLabel({ authorName: displayName })}
          </span>
        </Tag>
      </Tooltip>
    );
  }
);

PromptAuthor.displayName = "PromptAuthor";
